import {
  ArrayColumnOperators,
  BooleanColumnOperators,
  CommonColumnOperators,
  DateColumnOperators,
  NumberColumnOperators,
  StringColumnOperators,
} from "./enum.filterOperators";
import { FieldType, GenericTableSchema } from "./type.genericTableSchema";

export enum GenericTableFilterType {
  ARRAY = "array",
  BOOLEAN = "boolean",
  DATE = "date",
  NUMBER = "number",
  STRING = "string",
}

type BooleanFilterTypeOneValue = {
  option: BooleanColumnOperators;
  value: undefined | boolean;
};

export type BooleanFilterType = {
  type: GenericTableFilterType.BOOLEAN;
  filter: BooleanFilterTypeOneValue | ValueExistsFilterType;
};

export type ValueExistsFilterType = {
  option: CommonColumnOperators.VALUE_EXISTS;
  value: undefined | boolean;
};

type StringFilterTypeOneValue = {
  option:
    | StringColumnOperators.CONTAINS
    | StringColumnOperators.ENDS_WITH
    | StringColumnOperators.EXACT
    | StringColumnOperators.STARTS_WITH;
  value: undefined | string;
};
type StringFilterTypeArrayValue = {
  option: StringColumnOperators.IN;
  value: undefined | string[];
};
export type StringFilterType = {
  type: GenericTableFilterType.STRING;
  filter: StringFilterTypeArrayValue | StringFilterTypeOneValue | ValueExistsFilterType;
};

type NumberFilterTypeOneValue = {
  option: NumberColumnOperators.EXACT | NumberColumnOperators.LARGER_THEN | NumberColumnOperators.SMALLER_THEN;
  value: undefined | number;
};

type NumberFilterTypeTwoValues = {
  option: NumberColumnOperators.BETWEEN;
  value: undefined | [number, number];
};

type NumberFilterTypeArrayValue = {
  option: NumberColumnOperators.IN;
  value: undefined | number[];
};

export type NumberFilterType = {
  type: GenericTableFilterType.NUMBER;
  filter: NumberFilterTypeArrayValue | NumberFilterTypeOneValue | NumberFilterTypeTwoValues | ValueExistsFilterType;
};

type DateFilterTypeOneValue = {
  option: DateColumnOperators.AFTER | DateColumnOperators.BEFORE | DateColumnOperators.EXACT;
  value: undefined | string;
};

type DateFilterTypeTwoValues = {
  option: DateColumnOperators.BETWEEN;
  value: undefined | [string, string];
};

export type DateFilterType = {
  type: GenericTableFilterType.DATE;
  filter: DateFilterTypeOneValue | DateFilterTypeTwoValues | ValueExistsFilterType;
};

type ArrayFilterTypeValue = {
  option: ArrayColumnOperators.CONTAINS_ANY;
  value: undefined | string[];
};

export type ArrayFilterType = {
  type: GenericTableFilterType.ARRAY;
  filter: ArrayFilterTypeValue | ValueExistsFilterType;
};

export type FilterValueType = ArrayFilterType | BooleanFilterType | DateFilterType | NumberFilterType | StringFilterType;

type GenericTableFilter = Record<string, FilterValueType>;

export enum SortingDirection {
  ASC = "asc",
  DESC = "desc",
}

export type GenericTableSortingItems = {
  fieldName: string;
  direction: SortingDirection;
}[];

type GenericTablePagination = {
  currentPage: number;
  pageSize: number;
};

export type GenericTableSearch = {
  searchTerm: string;
  searchColumns: string[];
};

export type GenericTableQuery<T extends GenericTableSchema> = {
  filters?: undefined | GenericTableFilter;
  sorting?: undefined | GenericTableSortingItems;
  pagination?: undefined | GenericTablePagination;
  search?: undefined | GenericTableSearch;
  select?: undefined | (keyof T["fields"])[];
};

export function isArrayFilter(value: FilterValueType): value is ArrayFilterType {
  return value.type === GenericTableFilterType.ARRAY;
}

export function isStringFilter(value: FilterValueType): value is StringFilterType {
  return value.type === GenericTableFilterType.STRING;
}

export function isNumberFilter(value: FilterValueType): value is NumberFilterType {
  return value.type === GenericTableFilterType.NUMBER;
}

export function isBooleanFilter(value: FilterValueType): value is BooleanFilterType {
  return value.type === GenericTableFilterType.BOOLEAN;
}

export function isDateFilter(value: FilterValueType): value is DateFilterType {
  return value.type === GenericTableFilterType.DATE;
}

export type EntityFromGenericTableQuery<
  T extends GenericTableSchema,
  S extends GenericTableQuery<T>,
> = S["select"] extends (keyof T["fields"])[]
  ? {
      [K in keyof T["fields"] as K extends S["select"][number] ? K : never]: FieldType<T["fields"][K]>;
    }
  : {
      [K in keyof T["fields"]]: FieldType<T["fields"][K]>;
    };
